// client/src/pages/Admin/ProjectManager/utils/projectStatistics.ts
/**
 * PROJECT STATISTICS
 * 
 * This file contains statistical calculations for projects.
 * 
 * @module ProjectManager/Utils/Statistics
 */

import { Project, ProjectStats, ProjectCategory, ProjectStatus } from '../types/project.types';
import { ProjectCategoryLabels, ProjectStatusLabels } from '../types/project.types';
import { getProjectStatusCounts } from './projectHelpers';

// ============================================
// BASIC STATISTICS
// ============================================

/**
 * Get project statistics
 * @param projects - Array of projects
 * @returns Project statistics object
 */
export const getProjectStats = (projects: Project[]): ProjectStats => {
    if (!Array.isArray(projects)) {
        return { all: 0, draft: 0, published: 0, archived: 0, coming_soon: 0 };
    }
    return getProjectStatusCounts(projects);
};

/**
 * Get total project views
 * @param projects - Array of projects
 * @returns Total number of views
 */
export const getTotalViews = (projects: Project[]): number => {
    if (!Array.isArray(projects)) return 0;
    return projects.reduce((sum, project) => sum + (project.views || 0), 0);
};

/**
 * Get average project views
 * @param projects - Array of projects
 * @returns Average number of views
 */
export const getAverageViews = (projects: Project[]): number => {
    if (!Array.isArray(projects) || projects.length === 0) return 0;
    const total = getTotalViews(projects);
    return Math.round(total / projects.length);
};

/**
 * Get total number of featured projects
 * @param projects - Array of projects
 * @returns Number of featured projects
 */
export const getFeaturedCount = (projects: Project[]): number => {
    if (!Array.isArray(projects)) return 0;
    return projects.filter(p => p.featured).length;
};

/**
 * Get total number of ongoing projects
 * @param projects - Array of projects
 * @returns Number of ongoing projects
 */
export const getOngoingCount = (projects: Project[]): number => {
    if (!Array.isArray(projects)) return 0;
    return projects.filter(p => p.isOngoing).length;
};

// ============================================
// CATEGORY STATISTICS
// ============================================

/**
 * Get project counts by category
 * @param projects - Array of projects
 * @returns Object with category counts
 */
export const getCategoryCounts = (projects: Project[]): Record<ProjectCategory, number> => {
    if (!Array.isArray(projects)) {
        return {} as Record<ProjectCategory, number>;
    }

    const counts: Partial<Record<ProjectCategory, number>> = {};
    
    projects.forEach(project => {
        const category = project.category;
        counts[category] = (counts[category] || 0) + 1;
    });

    return counts as Record<ProjectCategory, number>;
};

/**
 * Get category distribution as percentages
 * @param projects - Array of projects
 * @returns Object with category percentages
 */
export const getCategoryPercentages = (projects: Project[]): Record<ProjectCategory, number> => {
    if (!Array.isArray(projects) || projects.length === 0) {
        return {} as Record<ProjectCategory, number>;
    }

    const counts = getCategoryCounts(projects);
    const total = projects.length;
    const percentages: Partial<Record<ProjectCategory, number>> = {};

    Object.entries(counts).forEach(([category, count]) => {
        percentages[category as ProjectCategory] = Math.round((count / total) * 100);
    });

    return percentages as Record<ProjectCategory, number>;
};

// ============================================
// IMPACT STATISTICS
// ============================================

/**
 * Get project counts by impact level
 * @param projects - Array of projects
 * @returns Object with impact counts
 */
export const getImpactCounts = (projects: Project[]): Record<string, number> => {
    if (!Array.isArray(projects)) {
        return { low: 0, medium: 0, high: 0, critical: 0 };
    }

    const counts = { low: 0, medium: 0, high: 0, critical: 0 };
    
    projects.forEach(project => {
        const impact = project.impact || 'low';
        counts[impact] = (counts[impact] || 0) + 1;
    });

    return counts;
};

/**
 * Get average impact score
 * @param projects - Array of projects
 * @returns Average impact score (1-4)
 */
export const getAverageImpactScore = (projects: Project[]): number => {
    if (!Array.isArray(projects) || projects.length === 0) return 0;

    const impactScores = {
        low: 1,
        medium: 2,
        high: 3,
        critical: 4
    };

    const total = projects.reduce((sum, project) => {
        const score = impactScores[project.impact || 'low'] || 0;
        return sum + score;
    }, 0);

    return Math.round((total / projects.length) * 100) / 100;
};

// ============================================
// FILTER OPTIONS STATISTICS
// ============================================

/**
 * Get unique filter options from projects
 * @param projects - Array of projects
 * @returns Filter options object
 */
export const getFilterOptions = (projects: Project[]) => {
    if (!Array.isArray(projects)) {
        return {
            categories: [],
            statuses: [],
            impacts: []
        };
    }

    const categories = new Set<ProjectCategory>();
    const statuses = new Set<ProjectStatus>();
    const impacts = new Set<string>();

    projects.forEach(project => {
        if (project.category) categories.add(project.category);
        if (project.status) statuses.add(project.status);
        if (project.impact) impacts.add(project.impact);
    });

    return {
        categories: Array.from(categories),
        statuses: Array.from(statuses),
        impacts: Array.from(impacts)
    };
};

/**
 * Get project counts by month (for charting)
 * @param projects - Array of projects
 * @param months - Number of months to look back (default: 12)
 * @returns Object with monthly counts
 */
export const getMonthlyProjectCounts = (
    projects: Project[],
    months: number = 12
): Array<{ month: string; count: number }> => {
    if (!Array.isArray(projects)) return [];

    const now = new Date();
    const counts: Record<string, number> = {};

    // Initialize all months
    for (let i = months - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setMonth(date.getMonth() - i);
        const key = date.toISOString().slice(0, 7); // YYYY-MM
        counts[key] = 0;
    }

    // Count projects by month
    projects.forEach(project => {
        if (!project.createdAt) return;
        try {
            const date = typeof project.createdAt === 'string' 
                ? new Date(project.createdAt) 
                : project.createdAt;
            const key = date.toISOString().slice(0, 7);
            if (counts[key] !== undefined) {
                counts[key]++;
            }
        } catch {
            // Ignore invalid dates
        }
    });

    return Object.entries(counts).map(([month, count]) => ({
        month,
        count
    }));
};

// ============================================
// SUMMARY STATISTICS
// ============================================

/**
 * Get comprehensive project statistics
 * @param projects - Array of projects
 * @returns Comprehensive statistics object
 */
export const getComprehensiveStats = (projects: Project[]) => {
    if (!Array.isArray(projects)) {
        return {
            total: 0,
            published: 0,
            draft: 0,
            archived: 0,
            coming_soon: 0,
            featured: 0,
            ongoing: 0,
            totalViews: 0,
            averageViews: 0,
            categories: {},
            impacts: { low: 0, medium: 0, high: 0, critical: 0 },
            categoryPercentages: {},
            impactPercentages: { low: 0, medium: 0, high: 0, critical: 0 }
        };
    }

    const total = projects.length;
    const statusCounts = getProjectStatusCounts(projects);
    const categoryCounts = getCategoryCounts(projects);
    const impactCounts = getImpactCounts(projects);
    const totalViews = getTotalViews(projects);
    const featured = getFeaturedCount(projects);
    const ongoing = getOngoingCount(projects);

    // Calculate percentages
    const categoryPercentages: Record<string, number> = {};
    Object.entries(categoryCounts).forEach(([category, count]) => {
        categoryPercentages[category] = Math.round((count / total) * 100);
    });

    const impactPercentages: Record<string, number> = {};
    Object.entries(impactCounts).forEach(([impact, count]) => {
        impactPercentages[impact] = Math.round((count / total) * 100);
    });

    return {
        total,
        published: statusCounts.published,
        draft: statusCounts.draft,
        archived: statusCounts.archived,
        coming_soon: statusCounts.coming_soon,
        featured,
        ongoing,
        totalViews,
        averageViews: getAverageViews(projects),
        categories: categoryCounts,
        impacts: impactCounts,
        categoryPercentages,
        impactPercentages
    };
};